import React from 'react'
import { InputGroup, Spinner } from '@blueprintjs/core'
import Downshift from 'downshift'
import { IRecord } from '@api/types'
import { Flex, Icon, IconButton } from '@ui'
import { KeyCodes } from '@constants'
import css from './searchDropdown.module.scss'

interface ISearch {
  run: (value: any) => void
  items: IRecord[]
  query: string
  loading?: boolean
  reload?: () => void
}
interface QueryTablePromptProps {
  closeMenu: () => void
  queryTable?: (query: string) => void
  search: ISearch
  queryTableMessage?: string
}
interface Props {
  search: ISearch
  onSelect: (value: any) => void
  renderItem: (item: any) => void
  queryTable?: (value: any) => void
  width?: string
  'data-input'?: string
  inputPlaceholder?: string
  queryTableMessage?: string
}
interface RightElementProps {
  closeMenu: () => void
  queryTable?: (query: string) => void
  search: ISearch
  onClear: () => void
  style: any
}

export default function Dropdown(props: Props) {
  const {
    search,
    onSelect,
    queryTable,
    renderItem,
    width = '200px',
    inputPlaceholder = 'Search...',
    queryTableMessage,
  } = props
  return (
    <Downshift
      onSelect={(selection: any, helpers: any) => {
        if (search.loading) {
          helpers.openMenu()
          return
        }
        onSelect(selection)
      }}
      itemToString={(item) => (item ? item.name : null)}
    >
      {({
        openMenu,
        closeMenu,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        isOpen,
        highlightedIndex,
      }) => (
        <Menu width={width} {...getRootProps()}>
          {/* eslint-disable @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <InputGroup
            className={css.search_input}
            {...getInputProps({
              fill: true,
              value: search.query,
              onChange: ({ target: { value } }: any) => {
                search.run(value)
                queryTable && !value && queryTable(value)
              },
              'data-input': props['data-input'],
              placeholder: inputPlaceholder,
              leftIcon: (
                <span>
                  <Icon name="search" className={css.search_icon} />
                </span>
              ),
              rightElement: (
                <RightElement
                  {...{
                    closeMenu,
                    queryTable,
                    search,
                    onClear: () => search.run(''),
                    style: { position: 'relative', top: 10 },
                  }}
                />
              ),
              onFocus: ({ target }: { target: any }) => {
                if (search.query || search.items.length) {
                  openMenu()
                  search.reload && search.reload()
                  target.select()
                }
              },
              onKeyDown: (event: any) => {
                if (
                  event.keyCode === KeyCodes.RETURN &&
                  search.query &&
                  highlightedIndex === null
                ) {
                  event.nativeEvent.preventDownshiftDefault = true
                  queryTable && queryTable(search.query)
                  closeMenu()
                }
              },
            })}
          />
          {isOpen && (
            <div className="search__results" {...getMenuProps()}>
              <NoResults search={search} queryTable={queryTable} />
              <QueryTablePrompt {...{ closeMenu, queryTable, search, queryTableMessage }} />
              {search.items.map((item, index) => {
                const active = index === highlightedIndex
                return (
                  <div
                    className={`search__result${active ? ' active' : ''}`}
                    {...getItemProps({
                      item,
                      key: `search_result-${item.id}`,
                    })}
                  >
                    {renderItem(item)}
                  </div>
                )
              })}
            </div>
          )}
        </Menu>
      )}
    </Downshift>
  )
}

const NoResults = ({
  search,
  queryTable,
}: {
  search: ISearch
  queryTable?: (query: string) => void
}) => {
  if (queryTable) {
    return null
  }
  if (!search.query || search.loading || search.items.length) return null
  return <div className={css.no_results_container}>No results</div>
}
const QueryTablePrompt = ({
  queryTable,
  search,
  closeMenu,
  queryTableMessage,
}: QueryTablePromptProps) => {
  if (!queryTable || !search.query) return null

  return (
    <Flex
      justify="space-between"
      onClick={() => {
        queryTable(search.query)
        closeMenu()
      }}
      style={{
        cursor: 'pointer',
        padding: 10,
        borderBottom: '1px solid var(--border-color)',
      }}
    >
      <div>
        <Icon name="search" style={{ paddingRight: 14, color: '#999' }} />
        {queryTableMessage ? (
          <>
            {queryTableMessage} <strong>{search.query}</strong>
          </>
        ) : (
          <>
            See all results for <strong>{search.query}</strong>
          </>
        )}
      </div>
    </Flex>
  )
}
const RightElement = ({ closeMenu, queryTable, search, onClear, style }: RightElementProps) => {
  if (search.loading) return <Spinner size={18} />
  if (search.query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear()
          queryTable && queryTable('')
          closeMenu()
        }}
        style={style}
      />
    )
  return null
}

const Menu = React.forwardRef<HTMLDivElement, MenuProps>(({ width = 'auto', ...props }, ref) => {
  return <div ref={ref} className={css.search_dropdown__menu} {...props} />
})

interface MenuProps {
  readonly width?: number
}
